import { createContext } from 'react';
import type { useParams } from 'react-router';
import type { SetOptional, Simplify } from 'type-fest';

import type Step from 'components/core/createModify/interfaces/step';
import type StepField from 'components/core/createModify/interfaces/stepField';
import type Tier from 'components/core/createModify/interfaces/tier';
import type { StepId } from 'components/sections/createModify/generated/builderTypes';
import type { GlobalDialogContextInterface } from 'contexts/GlobalDialogContext';
import type { UserContextInterface } from 'contexts/UserContext';
import type { CreateModifyTiers } from 'enums/createModifyTiers';
import type { ContextKindsArgs, FeatureFlagSet } from 'hooks/useFeatureFlags';
import type { useRouter } from 'hooks/useRouter';

export type BuilderLaunchSettings<
  TData extends Record<string, any> = any,
  TMetadata extends Record<string, any> = any,
  TSeededData extends Record<string, any> = any,
> = Omit<ToggleTierData<TData, TMetadata, TSeededData>, 'data' | 'metadata'>;

export interface CreateModifyContextInterface<
  TData extends Record<string, any> = any,
  TMetadata extends Record<string, any> = any,
  TSeededData extends Record<string, any> = any,
> {
  /**
   * Returns the field configuration for a specific field within a step.
   * Does not work with resolver-based fields.
   */
  getField: (
    step: Step<TData, TMetadata, TSeededData>,
    field: string
  ) => StepField<TData, TMetadata, TSeededData> | undefined;
  /**
   * Returns the tier data for a given tier ID.
   * Reads synchronously from the Zustand store via get().
   */
  getTier: (tierId: CreateModifyTiers) => Tier<TData, TMetadata, TSeededData> | undefined;
  /**
   * Updates an existing tier with partial data.
   * Required keys (data, metadata, tierId, type) cannot be overwritten with undefined.
   */
  setTier: (tierId: CreateModifyTiers, tierUpdates: Partial<Tier<TData, TMetadata, TSeededData>>) => void;
  /**
   * Collection of sub-contexts available within CreateModify.
   */
  subContexts: SubContextsType;
  /**
   * Map of all current tiers by their tier ID.
   */
  tiers: Record<string, Tier<TData, TMetadata, TSeededData> | undefined>;
  /**
   * Instantiates or removes tiers.
   * Pass tierData to create/update a tier, or omit it to remove the tier.
   * Automatically resolves activeStep from activeField (or activeStepId) if not provided.
   *
   * Note: Method has its own generics because nested tiers (e.g., TIER_1) often have
   * different data types than the parent tier.
   */
  toggleTier: <
    TTierData extends Record<string, any> = TData,
    TTierMetadata extends Record<string, any> = TMetadata,
    TTierSeededData extends Record<string, any> = TSeededData,
  >(
    tierId: CreateModifyTiers,
    tierData?: ToggleTierData<TTierData, TTierMetadata, TTierSeededData>
  ) => void;
  /**
   * The top-most (currently active) tier with feature flag filtering applied.
   */
  topTier: Tier<TData, TMetadata, TSeededData> | undefined;
}

/**
 * The value held on React context by `CreateModifyProvider`. A subset of
 * {@link CreateModifyContextInterface}: `tiers` and `topTier` now live in `useCreateModifyStore` and
 * are merged back in by the `useCreateModify` hook, so they are intentionally absent from the context
 * value. Spreading the store's stable action bundle onto this value keeps class consumers (e.g.
 * `ItemsTable`'s `static contextType`) and propped-context consumers working unchanged.
 */
export type CreateModifyContextValue<
  TData extends Record<string, any> = any,
  TMetadata extends Record<string, any> = any,
  TSeededData extends Record<string, any> = any,
> = Pick<
  CreateModifyContextInterface<TData, TMetadata, TSeededData>,
  'getField' | 'getTier' | 'setTier' | 'subContexts' | 'toggleTier'
>;

export type SubContextsType = {
  featureFlags: FeatureFlagSet;
  globalDialogContext: GlobalDialogContextInterface;
  routerContext: ReturnType<typeof useRouter> & { params: ReturnType<typeof useParams> };
  /**
   * A function to update the feature flag context for specified context kinds.
   *
   * @param contextKinds - An array of context kinds to update.
   * @returns A promise that resolves with the updated FeatureFlagSet, or undefined if the update fails.
   */
  updateFeatureFlagContext: (contextKinds: ContextKindsArgs[]) => Promise<FeatureFlagSet | undefined>;
  userContext: UserContextInterface;
};

/**
 * Input type for toggleTier. Extends Tier with additional resolution options.
 */
export type ToggleTierData<
  TData extends Record<string, any> = any,
  TMetadata extends Record<string, any> = any,
  TSeededData extends Record<string, any> = any,
> = Simplify<
  SetOptional<
    Omit<Tier<TData, TMetadata, TSeededData>, 'activeStep' | 'openedFromPending' | 'tierId'>,
    'data' | 'metadata'
  > & {
    /**
     * Explicitly specifies which step to activate by its ID.
     *
     * While `activeField` can auto-resolve to a step (by finding which step contains that field),
     * `activeStepId` is needed when:
     * 1. The same field key exists in multiple steps (ambiguous resolution)
     * 2. You want to navigate to a step without focusing any specific field
     * 3. The step uses dynamic/resolver-based fields that can't be searched at resolution time
     *
     * When both are provided, `activeStepId` takes precedence for step selection,
     * and `activeField` is used only for field focus within that step.
     */
    activeStepId?: StepId;
  }
>;

export const CreateModifyContext = createContext<CreateModifyContextValue | undefined>(undefined);
